import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Clock, Flame } from "lucide-react";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import api from "@/api";
import Seo, { breadcrumbSchema, localBusinessSchema, webPageSchema } from "@/components/Seo";

const KULHAD_IMG = "https://images.unsplash.com/photo-1619581073186-5b4ae1b0caad?crop=entropy&cs=srgb&fm=jpg&q=85";
const POUR_IMG = "/pour-hero.webp";
const CAFE_IMG = "/adda-pic.webp";
const SAMOSA_IMG = "/kulhad-spices.webp";
const LEAVES_IMG = "https://images.unsplash.com/photo-1606163017137-888c0177b3dd?crop=entropy&cs=srgb&fm=jpg&q=85";

const CHAPTERS = [
  { n: "01", title: "The Leaf", text: "We journey through tea gardens, learning from seasoned experts — selecting only the strongest Assam CTC and the most fragrant second flush. Every blend begins long before the kettle.", img: "https://images.unsplash.com/photo-1764012393710-be2d33971ac0?fm=jpg&q=80&w=1600&auto=format&fit=crop" },
  { n: "02", title: "The Brew", text: "A kadak chai is not boiled, it is persuaded. Milk rises slowly, ginger is crushed at the exact second the bubbles turn, and the final high pour folds air into every glass.", img: "/brew-pour.webp" },
  { n: "03", title: "The Kulhad", text: "Every chai is served in hand-thrown, unglazed clay. The kulhad drinks a little of the chai and gives back an earthy note no paper cup can imitate. Used once, returned to the earth.", img: "/kulhad-kesar.webp" },
  { n: "04", title: "The Adda", text: "A chai counter is India's oldest social network. Five outlets across Bengaluru, open 8 to midnight — because the best conversations were never scheduled.", img: CAFE_IMG },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Chai Times"
        description="Chai Times serves kadak kulhad chai, coffee, snacks and café favourites across five Bengaluru outlets. Explore the menu, find your nearest adda, or own a franchise."
        path="/"
        image="/kulhads-many.webp"
        schema={[
          webPageSchema({
            name: "Chai Times | Kulhad Chai Café in Bengaluru",
            description: "Kadak kulhad chai, coffee, snacks and warm addas across five Bengaluru outlets.",
            path: "/",
          }),
          breadcrumbSchema([{ name: "Home", path: "/" }]),
          ...locations.map(localBusinessSchema),
        ]}
      />
      {/* Kinetic hero */}
      <section ref={heroRef} className="relative min-h-screen bg-forest text-cream overflow-hidden grain" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-32 md:pt-40 pb-16 grid lg:grid-cols-12 gap-10 items-center relative z-10">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-6 flex items-center gap-3"
            >
              <Flame size={14} /> Kulhad Chai — Bengaluru
            </motion.p>
            <h1 className="font-serif font-black leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-8xl" data-testid="hero-heading">
              <MaskedLine delay={0.35}>More than</MaskedLine>
              <MaskedLine delay={0.5}>chai, it's an</MaskedLine>
              <MaskedLine delay={0.65} className="italic text-terracotta">experience.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="mt-7 max-w-md text-cream/70 leading-relaxed"
            >
              Chai Times is a kulhad chai café in Bengaluru — hand-thrown kulhads, slow-simmered brews, and five addas where every cup is a small ceremony.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.8 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link to="/menu" data-testid="hero-menu-btn" className="rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] bg-terracotta text-cream hover:bg-cream hover:text-charcoal transition-colors inline-flex items-center gap-2">
                View Menu <ArrowRight size={14} />
              </Link>
              <Link to="/franchise" data-testid="hero-franchise-btn" className="rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] border border-cream/30 text-cream hover:border-gold hover:text-gold transition-colors">
                Own an Outlet
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ delay: 0.7, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-3xl aspect-[4/5] overflow-hidden"
            >
              <motion.video
                src="/chai-hero.mp4"
                poster="/kulhad-kesar.webp"
                autoPlay
                muted
                loop
                playsInline
                style={{ y: imgY }}
                className="w-full h-[120%] object-cover scale-110"
                data-testid="hero-video"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, rotate: 8, scale: 0.8 }}
              animate={{ opacity: 1, rotate: -6, scale: 1 }}
              transition={{ delay: 1.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -bottom-8 -left-8 w-28 h-28 md:w-36 md:h-36"
            >
              <img src="/logo.png" alt="Chai Times badge" className="w-full h-full object-contain drop-shadow-2xl" data-testid="hero-logo-badge" />
            </motion.div>
          </div>
        </div>
        <div className="relative z-10 border-t border-cream/10">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 grid grid-cols-3 gap-4 font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-cream/50">
            <span>5 Outlets</span>
            <span className="text-center">40+ Brews & Bites</span>
            <span className="text-right">8 AM – 12 AM</span>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* Manifesto chapters */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-36" data-testid="manifesto-section">
        <Reveal>
          <SectionTag>The Manifesto</SectionTag>
          <h2 className="font-serif font-black text-4xl md:text-6xl tracking-tight mt-4 mb-20 max-w-3xl">
            Four chapters of <span className="italic text-terracotta">one perfect cup</span>
          </h2>
        </Reveal>
        <div className="space-y-24 md:space-y-36">
          {CHAPTERS.map((c, i) => (
            <div key={c.n} className={`grid md:grid-cols-12 gap-8 md:gap-14 items-center ${i % 2 ? "" : ""}`}>
              <div className={`md:col-span-6 ${i % 2 ? "md:order-2" : ""}`}>
                <Reveal>
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/3] group">
                    <img src={c.img} alt={`${c.title} — Chai Times kulhad chai in Bengaluru`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute top-4 left-4 font-mono text-xs bg-forest/80 text-cream px-3 py-1.5 rounded-full tracking-[0.2em]">{c.n}</span>
                  </div>
                </Reveal>
              </div>
              <div className={`md:col-span-6 ${i % 2 ? "md:order-1" : ""}`}>
                <Reveal delay={0.1}>
                  <span className="font-serif font-black text-7xl md:text-8xl text-clayline/70 leading-none block">{c.n}</span>
                  <h3 className="font-serif font-bold text-3xl md:text-4xl mt-2 mb-5">{c.title}</h3>
                  <p className="text-charcoal/70 leading-relaxed max-w-md">{c.text}</p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bento quick links */}
      <section className="bg-sand/60 py-24 md:py-32 border-y border-clayline/60" data-testid="bento-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <SectionTag>Explore Chai Times</SectionTag>
            <h2 className="font-serif font-black text-4xl md:text-5xl tracking-tight mt-4 mb-14">Pick your <span className="italic text-terracotta">poison</span></h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 auto-rows-[220px]">
            {[
              { to: "/menu", title: "The Menu", sub: "Kulhad chais, vada pav & bites", img: SAMOSA_IMG, span: "md:row-span-2", tid: "bento-menu" },
              { to: "/franchise", title: "Franchise", sub: "Own a Chai Times", img: CAFE_IMG, span: "", tid: "bento-franchise" },
              { to: "/journal", title: "The Journal", sub: "Stories from the kettle", img: POUR_IMG, span: "md:col-span-2", tid: "bento-journal" },
            ].map((b, i) => (
              <Reveal key={b.to} delay={i * 0.08} className={b.span}>
                <Link to={b.to} data-testid={b.tid} className="group relative block w-full h-full rounded-2xl overflow-hidden border border-clayline">
                  <img src={b.img} alt={`${b.title} — Chai Times Bengaluru`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/25 to-transparent" />
                  <div className="absolute bottom-0 p-6 text-cream flex items-end justify-between w-full">
                    <div>
                      <h3 className="font-serif font-bold text-2xl">{b.title}</h3>
                      <p className="text-cream/70 text-sm mt-1">{b.sub}</p>
                    </div>
                    <span className="w-10 h-10 rounded-full border border-cream/40 flex items-center justify-center group-hover:bg-terracotta group-hover:border-terracotta transition-colors">
                      <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Outlets strip */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-32" data-testid="outlets-strip">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <SectionTag>Find Your Adda</SectionTag>
            <h2 className="font-serif font-black text-4xl md:text-5xl tracking-tight mt-4">Five corners of <span className="italic text-terracotta">Bengaluru</span></h2>
          </div>
          <Link to="/locations" data-testid="outlets-view-all" className="font-mono text-xs uppercase tracking-[0.2em] text-terracotta hover:text-charcoal transition-colors inline-flex items-center gap-2">
            All outlets <ArrowRight size={14} />
          </Link>
        </Reveal>
        <div className="flex gap-5 overflow-x-auto pb-4 -mx-5 px-5 md:mx-0 md:px-0 snap-x">
          {locations.map((l, i) => (
            <Reveal key={l.id} delay={i * 0.06} className="min-w-[280px] snap-start">
              <div className="border border-clayline rounded-2xl p-6 bg-cream h-full hover:shadow-xl transition-shadow duration-300">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-terracotta">{String(i + 1).padStart(2, "0")} — {l.area}</span>
                <h3 className="font-serif font-bold text-xl mt-3 mb-3">{l.name}</h3>
                <p className="text-sm text-charcoal/60 flex gap-2"><MapPin size={15} className="shrink-0 mt-0.5 text-terracotta" />{l.address}</p>
                <p className="text-sm text-charcoal/60 flex gap-2 mt-2 items-center"><Clock size={15} className="text-terracotta" />{l.hours}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Franchise CTA */}
      <section className="relative bg-forest text-cream overflow-hidden grain" data-testid="franchise-cta">
        <video
          src="/franchise-loop.mp4"
          poster="/franchise-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          data-testid="franchise-cta-video"
        />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <Reveal>
              <SectionTag dark>Partner With Us</SectionTag>
              <h2 className="font-serif font-black text-4xl md:text-6xl tracking-tight mt-4 leading-[1.02]">
                Bengaluru drinks with us.<br /><span className="italic text-gold">Your city could be next.</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Reveal delay={0.15}>
              <Link to="/franchise" data-testid="cta-franchise-btn" className="rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] bg-terracotta text-cream hover:bg-gold hover:text-charcoal transition-colors inline-flex items-center gap-2">
                Claim a Territory <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
